import type { QueryClient } from '@tanstack/react-query';
import {
  FunnelInteractionType,
  type FunnelConversationDocument,
} from 'src/api/generated';
import { hasRecentMessageDeletion } from '../timeline-items/recent-events.util';
import {
  isInfiniteQueryData,
  isRegularQueryData,
  type InfiniteQueryData,
  type RegularQueryData,
} from 'src/modules/shared/utils/cache/update.util';

/**
 * Rejects a known-deleted Message preview without inferring a replacement.
 * @param queryClient - Owner of the bounded, tenant-scoped deletion overlay.
 * @param document - Search row or accepted conversation snapshot.
 * @returns Whether the preview still names an accepted deleted Message.
 */
export const hasDeletedMessagePreview = (
  queryClient: QueryClient,
  document: FunnelConversationDocument
): boolean =>
  document.lastInteraction?.modelType === FunnelInteractionType.MESSAGE &&
  hasRecentMessageDeletion(
    queryClient,
    document.tenantId,
    document.lastInteraction.id
  );

/**
 * Hides only the deleted preview; preserves business ordering and all other fields.
 * @param queryClient - Owner of accepted deletion authority.
 * @param document - HTTP or cached search row.
 * @returns Original row, or a row without the known-deleted preview.
 */
export const reconcileDeletedMessagePreview = (
  queryClient: QueryClient,
  document: FunnelConversationDocument
): FunnelConversationDocument =>
  hasDeletedMessagePreview(queryClient, document)
    ? { ...document, lastInteraction: undefined }
    : document;

/**
 * Clears known-deleted previews in current regular, infinite and detail caches.
 * @param queryClient - Owner of board caches and accepted deletions.
 */
export const clearDeletedMessagePreviews = (queryClient: QueryClient): void => {
  queryClient.setQueriesData<
    | InfiniteQueryData<FunnelConversationDocument>
    | RegularQueryData<FunnelConversationDocument>
    | FunnelConversationDocument
  >({ queryKey: ['crm/documents/funnel-conversations'] }, (data) => {
    if (!data) return data;
    const reconcile = (document: FunnelConversationDocument) =>
      reconcileDeletedMessagePreview(queryClient, document);
    if (isInfiniteQueryData<FunnelConversationDocument>(data)) {
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map(reconcile),
        })),
      };
    }
    if (isRegularQueryData<FunnelConversationDocument>(data)) {
      return { ...data, data: data.data.map(reconcile) };
    }
    return reconcile(data);
  });
};
